import { Smartphone, Copy, CheckCircle2, ExternalLink, Loader2, XCircle, Wifi } from "lucide-react";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "https://your-backend-url";

function CodeBlock({ children, lang = "ts" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative my-3">
      <button
        onClick={() => { navigator.clipboard.writeText(children); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="absolute top-2 right-2 p-1.5 rounded hover:bg-slate-700 text-slate-300 z-10"
        data-testid="copy-code-btn"
      >
        {copied ? <CheckCircle2 size={14} className="text-green-400" /> : <Copy size={14} />}
      </button>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

export default function MobileIntegrationDocsPage() {
  const [verify, setVerify] = useState({ status: "idle", detail: null });

  const runVerify = async () => {
    setVerify({ status: "running", detail: null });
    try {
      const t = Date.now();
      const res = await fetch(`${BACKEND}/api/socket.io/?EIO=4&transport=polling&t=${t}`);
      const txt = await res.text();
      const ms = Date.now() - t;
      const ok = res.status === 200 && /^[0-9]{1,2}\{"sid"/.test(txt);
      // Sample reply looks like: 0{"sid":"abc...","upgrades":["websocket"],...}
      let sidPreview = "";
      const m = txt.match(/"sid"\s*:\s*"([^"]{4,12})/);
      if (m) sidPreview = m[1] + "…";
      setVerify({
        status: ok ? "ok" : "fail",
        detail: ok
          ? `HTTP ${res.status} · ${ms}ms · Engine.IO handshake received · sid=${sidPreview}`
          : `HTTP ${res.status} · content-type=${res.headers.get("content-type") || "?"} · server didn't return an Engine.IO handshake. First 80 chars: ${txt.slice(0, 80)}`,
      });
    } catch (e) {
      setVerify({ status: "fail", detail: `Network error: ${e.message}` });
    }
  };
  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="mobile-integration-docs-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Smartphone className="text-[#8B1A1A]" size={28} /> Mobile Integration Guide
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Drop-in snippets for the Flutter / Expo mobile app. Backend URL: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{BACKEND}</code>
        </p>
      </div>

      <Card className="p-5 mb-4 border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
        <div className="flex items-start gap-3">
          <div className="text-2xl">⚠️</div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-amber-900 dark:text-amber-200 mb-1">Socket.IO path gotcha (read this first)</h2>
            <p className="text-sm text-amber-900 dark:text-amber-200 mb-2">
              The Socket.IO server is mounted at <code className="bg-amber-200 dark:bg-amber-900 px-1.5 py-0.5 rounded font-mono">/api/socket.io/</code> — <b>NOT</b> <code className="bg-amber-200 dark:bg-amber-900 px-1.5 py-0.5 rounded font-mono">/socket.io/</code>.
              The Kubernetes ingress only routes paths starting with <code>/api</code> to the backend; <code>/socket.io/</code> falls through to the React SPA and returns HTML (which is why mobile probes were getting <code>text/html</code> back).
            </p>
            <div className="space-y-1.5 text-xs font-mono">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">WRONG</Badge>
                <code>{`io("${BACKEND}")  // hits /socket.io/  → 200 text/html (React SPA)`}</code>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300">RIGHT</Badge>
                <code>{`io("${BACKEND}", { path: "/api/socket.io" })`}</code>
              </div>
            </div>
            <p className="text-xs text-amber-900 dark:text-amber-200 mt-2">
              Verify with curl: <code className="bg-amber-200 dark:bg-amber-900 px-1.5 py-0.5 rounded">{`curl -s "${BACKEND}/api/socket.io/?EIO=4&transport=polling"`}</code> → should return a JSON-ish handshake starting with <code>0{`{"sid":...}`}</code>.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                onClick={runVerify}
                disabled={verify.status === "running"}
                size="sm"
                className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white"
                data-testid="verify-socketio-btn"
              >
                {verify.status === "running" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wifi size={14} className="mr-1.5" />}
                {verify.status === "running" ? "Verifying…" : "Verify Socket.IO connection"}
              </Button>
              {verify.status === "ok" && (
                <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 text-xs font-semibold" data-testid="verify-socketio-ok">
                  <CheckCircle2 size={14} /> Connected
                </span>
              )}
              {verify.status === "fail" && (
                <span className="inline-flex items-center gap-1.5 text-red-700 dark:text-red-400 text-xs font-semibold" data-testid="verify-socketio-fail">
                  <XCircle size={14} /> Failed
                </span>
              )}
            </div>
            {verify.detail && (
              <div className={`mt-2 text-[11px] font-mono p-2 rounded ${verify.status === "ok" ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-900 dark:text-emerald-200" : "bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200"}`} data-testid="verify-socketio-detail">
                {verify.detail}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-5 mb-4 dark:bg-slate-800">
        <h2 className="text-lg font-semibold mb-2 dark:text-white">1 · Install dependencies</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">Expo / React Native:</p>
        <CodeBlock>{`yarn add socket.io-client @react-native-firebase/app @react-native-firebase/messaging`}</CodeBlock>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">Flutter (pubspec.yaml):</p>
        <CodeBlock>{`dependencies:
  socket_io_client: ^2.0.3+1
  firebase_core: ^3.6.0
  firebase_messaging: ^15.1.3`}</CodeBlock>
      </Card>

      <Card className="p-5 mb-4 dark:bg-slate-800">
        <h2 className="text-lg font-semibold mb-2 dark:text-white">2 · Connect to Socket.IO after login</h2>
        <p className="text-sm text-slate-500 mb-2">Pass the session token via the <code>auth</code> handshake. The backend looks it up against <code>user_sessions</code> and auto-joins rooms <code>user_&lt;id&gt;</code>, <code>role_&lt;ROLE&gt;</code>, <code>region_&lt;id&gt;</code> + every <code>chat_&lt;conv_id&gt;</code> you participate in.</p>
        <CodeBlock>{`// React Native / Expo
import { io } from "socket.io-client";

const socket = io("${BACKEND}", {
  path: "/api/socket.io",
  transports: ["websocket"],          // websocket first, falls back to polling
  auth: { token: sessionToken },      // from /api/v1/auth/login
  reconnection: true,
  reconnectionDelay: 1000,
});

socket.on("connect", () => console.log("[sio] connected", socket.id));
socket.on("connect_error", (e) => console.log("[sio] failed", e.message));
socket.on("connected", (data) => console.log("[sio] hello", data));`}</CodeBlock>
        <CodeBlock>{`// Flutter
import 'package:socket_io_client/socket_io_client.dart' as IO;

final socket = IO.io(
  '${BACKEND}',
  IO.OptionBuilder()
    .setPath('/api/socket.io')
    .setTransports(['websocket'])
    .setAuth({'token': sessionToken})
    .enableReconnection()
    .build(),
);
socket.connect();
socket.onConnect((_) => print('[sio] connected'));`}</CodeBlock>
      </Card>

      <Card className="p-5 mb-4 dark:bg-slate-800">
        <h2 className="text-lg font-semibold mb-2 dark:text-white">3 · Subscribe to the 13 server-emit events</h2>
        <div className="flex flex-wrap gap-1 mb-3">
          {["order_created", "order_status_changed", "order_edited", "dispatch_created", "dispatch_status_changed", "approval_action", "chat_message", "chat_typing", "gift_redeemed", "attendance_event", "gps_ping", "scheme_published", "settings_updated", "notification"].map(e => (
            <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>
          ))}
        </div>
        <CodeBlock>{`// chat_message: the Phase 2 chat fan-out
socket.on("chat_message", (msg) => {
  // {message_id, conversation_id, sender_id, sender_name, message_type,
  //  content, attachment, rich_card, reply_to_message_id, ttl_seconds, created_at}
  appendToConversation(msg.conversation_id, msg);
});

// notification: Flow Engine in-app push (NOTIFY node with IN_APP channel)
socket.on("notification", (n) => {
  // {title, body, data: {type, entity_id}, ts}
  showToast(n.title, n.body);
  if (n.data?.type) navigateToDeepLink(n.data.type, n.data.entity_id);
});

// Optional — typing indicator
socket.on("chat_typing", ({conversation_id, user_id, is_typing}) => { ... });

// Optional — order/scheme/dispatch real-time updates
socket.on("order_created", (o) => bumpAdminOrderQueue(o));
socket.on("scheme_published", (s) => refreshSchemesScreen());`}</CodeBlock>
      </Card>

      <Card className="p-5 mb-4 dark:bg-slate-800">
        <h2 className="text-lg font-semibold mb-2 dark:text-white">4 · Register FCM token</h2>
        <p className="text-sm text-slate-500 mb-2">After login, send the device's FCM token so push notifications can reach offline users.</p>
        <CodeBlock>{`import messaging from "@react-native-firebase/messaging";

const fcmToken = await messaging().getToken();
await fetch("${BACKEND}/api/v1/employees/me/fcm-token", {
  method: "PUT",
  headers: { "Content-Type": "application/json", "Authorization": \`Bearer \${sessionToken}\` },
  body: JSON.stringify({ fcm_token: fcmToken }),
});

// Foreground handler — Flow Engine pushes have data.type for deep linking
messaging().onMessage(async (msg) => {
  const { title, body } = msg.notification || {};
  showLocalNotification(title, body, msg.data);
});`}</CodeBlock>
      </Card>

      <Card className="p-5 mb-4 dark:bg-slate-800">
        <h2 className="text-lg font-semibold mb-2 dark:text-white">5 · Deep-link payload reference</h2>
        <p className="text-sm text-slate-500 mb-2">Every FCM push the Flow Engine sends carries a <code>data.type</code> field. Map these to screens:</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            ["ORDER_PLACED", "Order detail screen"],
            ["ORDER_STATUS", "Order status / approval"],
            ["DEALER_SUBMITTED", "Pending dealers queue"],
            ["DEALER_APPROVED", "My beat / dealer detail"],
            ["SCHEME_LAUNCHED", "Scheme detail"],
            ["TASK_ASSIGNED", "Tasks list"],
            ["COLLECTION", "Collections module"],
            ["LEAVE_STATUS", "Leave history"],
            ["EXPENSE_STATUS", "Expense detail"],
            ["GRIEVANCE_UPDATE", "Grievance thread"],
            ["GIFT_REDEEM", "Loyalty / Gifts"],
            ["CHAT_MESSAGE", "Open conversation (data.entity_id = conversation_id)"],
          ].map(([t, screen]) => (
            <div key={t} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700 rounded">
              <code className="font-mono text-amber-700 dark:text-amber-300">{t}</code>
              <span className="text-slate-500">→</span>
              <span className="text-slate-700 dark:text-slate-200">{screen}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 dark:bg-slate-800 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500">
        <h3 className="font-semibold dark:text-white mb-1">⚠️ Production checklist</h3>
        <ul className="text-sm text-slate-700 dark:text-slate-300 space-y-1 list-disc pl-5">
          <li>Re-connect on app foreground; backend ping interval is 25s.</li>
          <li>Reconnect after token refresh — Socket.IO needs the new token in <code>auth.token</code>.</li>
          <li>Don't double-render messages — if you're in the chat screen AND get FCM, dedupe by <code>message_id</code>.</li>
          <li>Save FCM token on every login (tokens can rotate per-install).</li>
        </ul>
      </Card>
    </div>
  );
}
